import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { pretty } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhoneCall, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function CallLogs() {
  const { rid } = useParams();
  const [calls, setCalls] = useState([]);
  const [view, setView] = useState(null);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/call-logs`).then((r) => setCalls(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const complete = async (c) => {
    await api.patch(`/call-logs/${c.id}/handover-complete`);
    toast.success("Handover completed"); load();
  };

  return (
    <div>
      <PageHeader title="Calls & AI" subtitle="Call logs, transcripts and handover requests from the AI receptionist." />
      <div className="space-y-3">
        {calls.map((c) => (
          <Card key={c.id} data-testid={`call-log-${c.id}`} className="p-5 rounded-2xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <PhoneCall className="h-4 w-4 text-primary" />
                  <p className="font-display font-bold">{c.caller_number}</p>
                  <Badge variant="outline" className={c.status === "IN_PROGRESS" ? "bg-accent/15 text-accent border-accent/30" : "bg-stone-100 text-stone-600"}>{pretty(c.status)}</Badge>
                  {c.intent && <Badge variant="outline">{pretty(c.intent)}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{c.summary}</p>
                {c.handover_requested && !c.handover_completed && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Handover: {c.handover_reason}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setView(c)} data-testid={`view-transcript-${c.id}`}>Transcript</Button>
                {c.handover_requested && !c.handover_completed && (
                  <Button size="sm" onClick={() => complete(c)} data-testid={`complete-handover-${c.id}`}>Mark handled</Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        {calls.length === 0 && <p className="text-muted-foreground">No calls logged yet. Calls appear here once the AI receptionist is live.</p>}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Transcript · {view?.caller_number}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(view?.transcript || []).map((t, i) => (
              <div key={i} className={`rounded-lg p-2.5 text-sm ${t.role === "assistant" ? "bg-primary/10" : "bg-secondary"}`}>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">{t.role === "assistant" ? "AI" : "Caller"}</span>
                <p>{t.text}</p>
              </div>
            ))}
            {(!view?.transcript || view.transcript.length === 0) && <p className="text-sm text-muted-foreground">No transcript recorded.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
